import { ComponentObservable, componentObserver } from '../../component-observer';
import { Icon } from '../../general/icons/Icon';
import {
  createHtmlElementFromString,
  createPatientGroupIconsFromString,
  createUseIconsFromId,
  getFallbackIconForCategory,
  removeAllHtmlTagsAndLineBreaks,
  formatText
} from '../../utils/html.utils';
import { overlay as overlayModule } from '../overlays/overlay.module';
import { accordion as accordionModule } from '../../components/accordion/accordion.module';
import { addItemToShoppingCart, getPriceWithCurrencyForOnePiece } from '../../api/shopping-cart-api';
import {
  createNewWishlist,
  deleteItemFromWishList,
  deleteWishlist,
  getUrlForPdf,
  getWishListsAndEasyReferenceCards,
  renameWishlist
} from '../../api/wish-list-api';
import { HamiltonProduct, HamiltonProductReduced, packagingUnitOrDefault } from '../../api/products-api';
import { getUrlForImage } from '../../utils/image.utils';
import { Param, writeToUrl } from '../../utils/url.utils';
import { wishListData } from '../../wish-list-data.module';
import { eCatalog } from '../e-catalog/e-catalog.module';
import { breakpointListener } from '../../utils/breakpoint-listener.module';
import { allowScrollingOnBody, preventScrollingOnBody } from '../../utils/body-scroll.utils';
import { currentBreakpoint } from '../../utils/resize.utils';
import { toastNotification } from '../toast-notification/toast-notification.module';
import { addToShoppingCart } from '../e-catalog/e-catalog.utils';

/**
 * Object that represents all data that must be displayed in the wish list.
 * Includes the wish list items and the easy reference card items.
 */
export interface HamiltonWishList {
  '@id': string;
  wishLists: HamiltonProductList[];
  easyReferenceLists: HamiltonProductList[];
}

/**
 * Object that represents a single product list (either wish list or easy reference cards).
 */
export interface HamiltonProductList {
  /**
   * ID of the wish list, that is used e.g. to rename/delete the list.
   */
  '@name': string;
  /**
   * Name for the list that is displayed to the user.
   */
  'mgnl:lastModified': string;
  /**
   * Name for the list that is displayed to the user.
   */
  name: string;
  /**
   * The products of a wish list.
   */
  items: HamiltonProduct[];

  /**
   * The products of an easy reference card.
   */
  articles: HamiltonProduct[];
}

/**
 * Initializes the wish list elements.
 */
class WishListModule implements ComponentObservable {
  /**
   * Selector of all elements that must be initialized in javascript.
   */
  componentSelector = '.wish-list';

  /**
   * Selector of the 'new wish list' overlay.
   */
  newWishListOverlaySelector = '#new-wish-list-overlay';

  /**
   * Selector of the 'rename wish list' overlay.
   */
  renameWishListOverlaySelector = '#rename-wish-list-overlay';

  /**
   * Selector of the 'delete wish list' overlay.
   */
  deleteWishListOverlaySelector = '#delete-wish-list-overlay';

  /**
   * Class that marks the wish list as expanded.
   */
  expandedClass = 'wish-list--expanded';

  /**
   * Class that hides/displays the wish list.
   */
  visibleClass = 'wish-list--visible';

  /**
   * Markup for the wish list item.
   */
  userId = '';

  wishListItemMarkup = `
  <div class="wish-list-item">
      <img src="" alt="Image of wish list item">
      <div class="wish-list-item__content">
        <span class="wish-list-item__product-id"></span>
        <a class="wish-list-item__product-label" role="button"></a>
        <div class="wish-list-item__addition-info-block">
          <p class="wish-list-item__description text-small"></p>      
          <table class="wish-list-item__table">
           <tbody>
            <tr class="wish-list-item__patient-groups">
              <th class="text-small"></th>
              <td>
              </td>
            </tr>
            <tr class="wish-list-item__usage">
              <th class="text-small"></th>
              <td></td>
            </tr>
            <tr class="wish-list-item__packing-unit">
              <th class="text-small"></th>
              <td><div class="packing-unit" title="Packing Unit"></div></td>
            </tr>
          </tbody></table>
        </div>
        <a class="wish-list-item__add-to-cart-button link--icon" role="button">${Icon.shoppingCart}</a>
      </div>
      <span class="wish-list-item__remove-item-button">${Icon.delete}</span>
  </div>
  `;

  /**
   * Markup for the accordion that holds all items of a wish list.
   */
  accordionMarkup = `
    <div class="accordion accordion--with-buttons accordion--closed">
      <button class="accordion__header"><span class="accordion__title"></span> ${Icon.edit} ${Icon.delete} ${Icon.pin}</button>
      <div class="accordion__content">
         <p class="wish-list__empty-list-hint"></p>
         <a class="wish-list-item__download-pdf-button link--icon" href="#" download>${Icon.pdf}</a>
      </div>
    </div>
  `;

  /**
   * One row in the 'additional info' section of a wish list item that
   * is only displayed when the wish list is expanded.
   */
  wishListItemAdditionalInfoRow = `
  <tr>
    <th class="text-small"></th>
    <td class="text-small text-color-dark"></td>
  </tr>`;

  /**
   * Create the wish list. Fetches the wish list and renders it.
   * @param wishListElement: The root element of the wish list.
   */
  createWishList(wishListElement: HTMLElement): void {
    // get the wish list data
    this.fetchWishListData()
      .then((wishListData) => {
        // Render table
        this.renderWishListWithData(wishListData, wishListElement);

        formatText(wishListElement);
      })
      .catch(() => {
        // no user data (logged out)
        this.hideTriggerButton();
      });

    // Render loading state while waiting for data
    this.renderWishListWithData(null, wishListElement);
  }

  /**
   * Retrieve the wish list data.
   */
  fetchWishListData(): Promise<HamiltonWishList> {
    return getWishListsAndEasyReferenceCards().then((lists: HamiltonWishList) => {
      this.userId = document.querySelector('html').dataset.uid;
      return lists;
    });
  }

  /**
   * Render the wish list.
   * @param wishList: The wish list data.
   * @param wishListElement: The wish list root element.
   */
  renderWishListWithData(wishList: HamiltonWishList, wishListElement: HTMLElement): void {
    // This is the container for all the wish lists
    const wishListsContainer = wishListElement.querySelector('.wish-list__wrapper') as HTMLElement;
    const easyReferenceListsContainer = wishListElement.querySelector('.wish-list__reference-cards') as HTMLElement;

    // clear the content before adding new elements (except for empty state element)
    const wishListEmptyState = wishListsContainer.firstElementChild;
    wishListsContainer.innerHTML = '';
    wishListsContainer.appendChild(wishListEmptyState);

    easyReferenceListsContainer.innerHTML = '';

    // find the last modified wish list and open it
    //const lastModifiedWishlist = this.getLastModifiedWishListId(wishList);

    // Render wish lists or empty state
    if (wishList && wishList.wishLists) {
      for (const wishListData of wishList.wishLists) {
        const wishListId = wishListData['@name'];
        const wishListUuid = wishListData['@id'];
        const wishListAccordion = createHtmlElementFromString(this.accordionMarkup);
        const wishListAccordionContent = wishListAccordion.querySelector('.accordion__content');
        const wishListAccordionPdfButton = wishListAccordionContent.querySelector<HTMLAnchorElement>('a');
        const wishListEmptyListHint =
          wishListAccordionContent.querySelector<HTMLElement>('.wish-list__empty-list-hint');
        wishListAccordion.querySelector<HTMLElement>('.accordion__title').innerText = wishListData.name;
        wishListAccordionContent.innerHTML = '';

        /*if (lastModifiedWishlist === wishListId) {
          // open the accordion that was previously opened (e.g. after reloading the wish list)
          accordionModule.setAccordionOpen(wishListAccordion);
        }*/

        if (window.localStorage) {
          if (window.localStorage.getItem('wishlistState' + this.userId)) {
            const wishlistState = window.localStorage.getItem('wishlistState' + this.userId).split(',');
            if (wishlistState.indexOf(wishListId) !== -1) {
              accordionModule.setAccordionOpen(wishListAccordion);
            }
          }
          const button = wishListAccordion.querySelector('.accordion__header') as HTMLElement;
          button.addEventListener('click', () => {
            setTimeout(() => {
              this.setWishlistStates();
            }, 500);
          }, false);
        }

        // add translation for download pdf button label and create the download link
        const pdfButtonLabel = document.createTextNode(wishListElement.dataset.downloadPdfButtonLabel);
        wishListAccordionPdfButton.appendChild(pdfButtonLabel);

        // check if the wish list is empty and if so, display a hint
        if (wishListData.items && wishListData.items.length > 0) {
          for (const wishListItemData of wishListData.items) {
            // for every product in this wish list, create an item
            console.log(wishListElement, wishListItemData)
            const wishListItemElement = this.createWishListItem(wishListElement, wishListItemData);
            wishListAccordionContent.appendChild(wishListItemElement);
            this.addListenerForDeleteItemButton(
              wishListElement,
              wishListItemElement,
              wishListItemData['@id'],
              wishListData.items.map((item) => item['@id']),
              wishListId
            );
            
            if (wishListItemData.price && wishListItemData.price.length > 0 && wishListItemData.isWebshopUser && wishListItemData.catalog_taxonomies.indexOf('8b403b52-321c-4a5a-abbc-0dd4fab6877e') > -1) {
              this.addListenerForAddToCartButton(
                wishListElement,
                wishListItemData,
                wishListItemElement,
                wishListItemData['@id']
              );
            } else {
              wishListItemElement.querySelector('.wish-list-item__add-to-cart-button').remove();
            }
          }

          this.addListenerForDownloadPdfButton(wishListAccordionPdfButton, wishListUuid);
          wishListAccordionContent.appendChild(wishListAccordionPdfButton);
        } else {
          this.addTranslationToEmptyStateHint(wishListElement, wishListEmptyListHint);
          wishListAccordionContent.appendChild(wishListEmptyListHint);
        }

        this.addListenerForRenameButton(wishListAccordion, wishListData.name, wishListId);
        this.addListenerForDeleteButton(wishListAccordion, wishListData.name, wishListId);
        this.addListenerForSetDefaultButton(wishListAccordion, wishListData.name, wishListId);

        // add the wish list to the sidebar
        wishListsContainer.appendChild(wishListAccordion);
      }

      wishListElement.classList.toggle('wish-list--empty', wishList?.wishLists?.length === 0);
    } else {
      this.renderLoadingState(wishListsContainer);
    }

    // Render easy reference cards or empty state
    if (wishList && wishList.easyReferenceLists) {
      for (const easyReferenceList of wishList.easyReferenceLists) {
        const easyReferenceCardId = easyReferenceList['@id'];
        const easyReferenceAccordion = createHtmlElementFromString(this.accordionMarkup);
        const wishListAccordionContent = easyReferenceAccordion.querySelector('.accordion__content');
        const wishListAccordionPdfButton = wishListAccordionContent.querySelector('a');
        easyReferenceAccordion.querySelector<HTMLElement>('.accordion__title').innerText = easyReferenceList.name;

        // No buttons in reference cards
        easyReferenceAccordion.querySelector('.accordion__header svg').remove();
        easyReferenceAccordion.querySelector('.accordion__header svg').remove();

        // add translation for download pdf button label
        const pdfButtonLabel = document.createTextNode(wishListElement.dataset.downloadPdfButtonLabel);
        easyReferenceAccordion
          .querySelector<HTMLElement>('.wish-list-item__download-pdf-button')
          .appendChild(pdfButtonLabel);

        for (const easyReferenceCardData of easyReferenceList.articles) {
          // for each reference card create an element
          const easyReferenceCard = this.createEasyReferenceCard(wishListElement, easyReferenceCardData);
          wishListAccordionContent.insertBefore(easyReferenceCard, wishListAccordionPdfButton);
        }

        this.addListenerForDownloadPdfButton(wishListAccordionPdfButton, easyReferenceCardId);

        // add the reference card to the sidebar
        easyReferenceListsContainer.appendChild(easyReferenceAccordion);
      }
    } else {
      this.renderLoadingState(easyReferenceListsContainer);
    }
    this.setHighlightForDefaultWishlist();
  }

  /**
   * Get the ID of the wish list that was last modified. If no wish list found, returns null.
   */
  getLastModifiedWishListId(wishList: HamiltonWishList): string {
    if (wishList?.wishLists) {
      const sortedListsByLastModifiedDate = [...wishList.wishLists].sort((listA, listB) => {
        return listA['mgnl:lastModified'] > listB['mgnl:lastModified']
          ? -1
          : listA['mgnl:lastModified'] < listB['mgnl:lastModified']
          ? 1
          : 0;
      });

      if (sortedListsByLastModifiedDate.length > 0) {
        return sortedListsByLastModifiedDate[0]['@name'];
      }
    }

    return null;
  }

  /**
   * Render skeleton rows in the given container.
   */
  renderLoadingState(listsContainer: HTMLElement): void {
    listsContainer.innerHTML += '<span class="wish-list__skeleton"></span><span class="wish-list__skeleton"></span>';
  }

  /**
   * Create an easy reference card (html) with the given data and return the element.
   */
  createEasyReferenceCard(root: HTMLElement, easyReferenceCardData: HamiltonProduct): HTMLElement {
    const wishListItemMarkup = createHtmlElementFromString(this.wishListItemMarkup);

    // now render the product details
    this.fillWithProductDetails(root, wishListItemMarkup, easyReferenceCardData);

    // no close and add to cart buttons
    wishListItemMarkup.querySelector('.wish-list-item__remove-item-button').remove();
    wishListItemMarkup.querySelector('.wish-list-item__add-to-cart-button').remove();

    return wishListItemMarkup;
  }

  /**
   * Populate the wish list item markup with the given product information. For example sets the image, product
   * name, description and so on.
   * @param root: Root element of the wish list
   * @param wishListItemMarkup: HTML element of the wish list item that should be filled
   * @param productInfo: The details about the product
   */
  fillWithProductDetails(root: HTMLElement, wishListItemMarkup: HTMLElement, productInfo: HamiltonProduct): void {
    const productName = wishListItemMarkup.querySelector<HTMLElement>('.wish-list-item__product-label');
    const wishListItemContent = wishListItemMarkup.querySelector('.wish-list-item__content');

    productName.innerText = productInfo.name;
    // Opens the product detail page and closes the wishlist on product name click
    productName.addEventListener('click', () => {
      writeToUrl({ param: Param.PRODUCT, value: productInfo['@id'] });
      this.toggleWishListVisibleState(root);
    });
    wishListItemMarkup.querySelector<HTMLElement>('.wish-list-item__product-id').innerText = productInfo.partNumber;
    wishListItemMarkup.querySelector<HTMLElement>('.wish-list-item__description').innerText =
      removeAllHtmlTagsAndLineBreaks(productInfo.description);

    const imageUrl = getUrlForImage(productInfo.image?.renditions?.small?.link);
    const itemImage = wishListItemMarkup.querySelector('img');
    if (imageUrl) {
      // if there is an image for the product, show it
      itemImage.src = imageUrl;
    } else {
      // otherwise show the large version of the category icon for this product
      wishListItemMarkup.removeChild(itemImage);
      const categoryIconLarge = getFallbackIconForCategory(productInfo.icons, true);
      wishListItemMarkup.insertBefore(categoryIconLarge, wishListItemContent);
    }
    // table with the additional product info, that we need to fill with details
    const additionalInfoTable = wishListItemMarkup.querySelector<HTMLTableElement>('.wish-list-item__table');

    let cellIndex = 0;
    // The following properties are optional and should only be displayed if they are available
    // The order is fixed and should be the following: length, compatibility, price
    if (productInfo.length) {
      const infoRow = additionalInfoTable.insertRow(cellIndex);
      infoRow.innerHTML = this.wishListItemAdditionalInfoRow;
      infoRow.querySelector('th').innerText = root.dataset.productLengthLabel;
      infoRow.querySelector('td').innerText = `${productInfo.length} cm`;
      cellIndex++;
    }

    if (productInfo.compatibility) {
      const infoRow = additionalInfoTable.insertRow(cellIndex);
      infoRow.innerHTML = this.wishListItemAdditionalInfoRow;
      infoRow.querySelector('th').innerText = root.dataset.productCompatibilityLabel;
      infoRow.querySelector('td').innerText = productInfo.compatibility;
      cellIndex++;
    }

    // create patient groups icons
    const patientGroupRow = wishListItemMarkup.querySelector('.wish-list-item__patient-groups');
    patientGroupRow.querySelector<HTMLElement>('th').innerText = root.dataset.productPatientGroupsLabel;
    const patientGroupsContainer = additionalInfoTable.querySelector('.wish-list-item__patient-groups td');
    if (productInfo.patientGroups) {
      const translations = {
        neonatal: root.dataset.patientGroupNeonatalTitle,
        pediatric: root.dataset.patientGroupPediatricTitle,
        adults: root.dataset.patientGroupAdultTitle,
        active: root.dataset.patientGroupActiveLabel,
        inactive: root.dataset.patientGroupInactiveLabel
      };
      const patientGroupInfoElements = createPatientGroupIconsFromString(productInfo.patientGroups, translations);
      patientGroupsContainer.appendChild(patientGroupInfoElements);
    } else {
      // hide the icon if the information is not available
      patientGroupRow.parentElement.removeChild(patientGroupRow);
    }

    // create usage icon
    const usageRow = wishListItemMarkup.querySelector('.wish-list-item__usage');
    usageRow.querySelector<HTMLElement>('th').innerText = root.dataset.productUsageLabel;
    const usageContainer = additionalInfoTable.querySelector('.wish-list-item__usage td');
    if (productInfo.typeOfUse) {
      const translations = {
        singleUse: root.dataset.usageIconSingleUseTitle,
        reusable: root.dataset.usageIconReusableTitle,
        autoclavable: root.dataset.usageIconAutoclavableTitle
      };
      const usageInfoElement = createUseIconsFromId(productInfo.typeOfUse, translations);
      usageContainer.appendChild(usageInfoElement);
    } else {
      // hide the icon if the information is not available
      usageRow.parentElement.removeChild(usageRow);
    }

    // create packaging unit icon if available
    const packagingUnitRow = wishListItemMarkup.querySelector('.wish-list-item__packing-unit');
    packagingUnitRow.querySelector<HTMLElement>('th').innerText = root.dataset.productPackagingUnitLabel;
    packagingUnitRow.querySelector<HTMLElement>('td .packing-unit').innerText = packagingUnitOrDefault(productInfo);

    if (productInfo.price) {
      const infoRow = additionalInfoTable.insertRow();
      infoRow.innerHTML = this.wishListItemAdditionalInfoRow;
      infoRow.querySelector('th').innerText = root.dataset.productPriceLabel;
      infoRow.querySelector('td').innerText = getPriceWithCurrencyForOnePiece(productInfo.price);
    }
  }

  /**
   * Listen for clicks on the edit button and open the rename wish list dialog.
   */
  addListenerForRenameButton(wishListAccordion: HTMLElement, wishListName: string, wishListId: string): void {
    const editButton = wishListAccordion.querySelectorAll('svg')[0];
    editButton.setAttribute('data-overlay-id', 'rename-wish-list-overlay');

    // store the name of the wish list, so that the overlay module can put it into the input field
    editButton.setAttribute('data-overlay-input-value', wishListName);
    // also store the id of the wish list
    editButton.setAttribute('data-overlay-associated-id', wishListId);

    // automatically focus the input field
    editButton.setAttribute('data-focus-input', 'true');
  }

  /**
   * Listen for clicks on the delete button and open the delete wish list dialog.
   */
  addListenerForDeleteButton(wishListAccordion: HTMLElement, wishListName: string, wishListId: string): void {
    const deleteButton = wishListAccordion.querySelectorAll('svg')[1];
    deleteButton.setAttribute('data-overlay-id', 'delete-wish-list-overlay');

    // Store the id of the wish list, so that it can be retrieved as soon as the user clicked the "confirm deletion" button
    deleteButton.setAttribute('data-overlay-associated-id', wishListId);
  }

  addListenerForSetDefaultButton(wishListAccordion: HTMLElement, wishListName: string, wishListId: string): void {
    const setDefaultWishlistButton = wishListAccordion.querySelectorAll('svg')[2];
    setDefaultWishlistButton.setAttribute('data-wishlist-id', wishListId);
    setDefaultWishlistButton.addEventListener('click', (e) => {
      e.stopImmediatePropagation();
      e.preventDefault();
      if (window.localStorage) {
        if (window.localStorage.getItem('defaultWishlist' + this.userId)) {
          if (window.localStorage.getItem('defaultWishlist' + this.userId) === wishListId) {
            window.localStorage.removeItem('defaultWishlist' + this.userId)
          }
        }
        window.localStorage.setItem('defaultWishlist' + this.userId, wishListId);
      }
      this.setHighlightForDefaultWishlist();
    })
  }

  setHighlightForDefaultWishlist() {
    if (window.localStorage) {
      const wishlists = document.querySelectorAll('.wish-list__wrapper .accordion');
      if (window.localStorage.getItem('defaultWishlist' + this.userId)) {
        const currentDefaultId = window.localStorage.getItem('defaultWishlist' + this.userId);

        for (let i = 0; i < wishlists.length; i++) {
          if (wishlists[i].querySelectorAll('svg')[2].getAttribute('data-wishlist-id') === currentDefaultId) {
            wishlists[i].classList.add('accordion--highlighted');
          } else {
            wishlists[i].classList.remove('accordion--highlighted');
          }
        }
      } else {
        for (let i = 0; i < wishlists.length; i++) {
          wishlists[i].classList.remove('accordion--highlighted');
        }
      }
    }
  }

  getWishlistStates() {
    if (window.localStorage) {
      if (window.localStorage.getItem('wishlistState' + this.userId)) {
        const interval = setInterval(() => {
          const wishlists = document.querySelectorAll('.wish-list__wrapper .accordion');
          const wishlistState = window.localStorage.getItem('wishlistState' + this.userId).split(',');
          if (wishlists.length > 0) {
            clearInterval(interval);
            for (let i = 0; i < wishlists.length; i++) {
              if (wishlistState.indexOf(wishlists[i].querySelectorAll('svg')[2].getAttribute('data-wishlist-id')) === -1) {
                wishlists[i].classList.add('accordion--closed');
              } else {
                wishlists[i].classList.remove('accordion--closed');
              }
            }
          }
        }, 100);
      }
    }
  }

  setWishlistStates() {
    if (window.localStorage) {
      const wishlists = document.querySelectorAll('.wish-list__wrapper .accordion');
      const openedWishlists = [];

      for (let i = 0; i < wishlists.length; i++) {
        if (!wishlists[i].classList.contains('accordion--closed')) {
          openedWishlists.push(wishlists[i].querySelectorAll('svg')[2].getAttribute('data-wishlist-id'))
        }
      }
      window.localStorage.setItem('wishlistState' + this.userId, openedWishlists.join(','))
    }
  }

  /**
   * Listen for clicks on the download pdf button.
   */
  addListenerForDownloadPdfButton(downloadButton: HTMLAnchorElement, wishListId: string): void {
    downloadButton.href = getUrlForPdf(wishListId);
  }

  /**
   * Listen for clicks on the delete button of a wish list item and removes the item from the list.
   */
  addListenerForDeleteItemButton(
    root: HTMLElement,
    wishListItemElem: HTMLElement,
    productId: string,
    currentProducts: string[],
    wishListId: string
  ): void {
    const deleteButton = wishListItemElem.querySelector('.wish-list-item__remove-item-button');

    deleteButton.addEventListener('click', () => {
      wishListItemElem.style.display = 'none';
      deleteItemFromWishList(productId, currentProducts, wishListId).then((success) => {
        if (success) {
          // reload the wish list, so that the deleted item disappears
          //this.reloadWishlist(root);
          wishListItemElem.remove();

          // also delete the item from the cached wish list product IDs and update the visible pin buttons in the eCatalogProducts
          wishListData.removeProductFromCachedWishList(productId);
          eCatalog.eCatalogProducts.updatePinButtonStates();
        } else {
          const errorMessage = root.dataset.errorMessage;
          toastNotification.createErrorToastMessage(errorMessage);
          wishListItemElem.style.display = 'block';
        }
      });
    });
  }

  /**
   * Listen for clicks on the 'add to cart' button, then add the product to the cart.
   */
  addListenerForAddToCartButton(
    root: HTMLElement,
    product: HamiltonProduct,
    wishListItem: HTMLElement,
    productId: string
  ): void {
    const addToCartButton = wishListItem.querySelector('.wish-list-item__add-to-cart-button');

    if (addToCartButton) {
      addToCartButton.addEventListener('click', () => {
        addItemToShoppingCart(productId).then((success) => {
          if (success) {
            const reducedProduct: HamiltonProductReduced = {
              productName: product.name,
              imageSmall: product.image?.renditions?.small?.link,
              nodeid: product['@id'],
              partNumber: product.partNumber,
              icons: product.icons
            };
            addToShoppingCart(
              reducedProduct,
              root.dataset.addedToShoppingCart,
              root.dataset.goToShoppingCart,
              root.dataset.errorMessage
            );
          } else {
            const errorMessage = root.dataset.errorMessage;
            toastNotification.createErrorToastMessage(errorMessage);
          }
        });
      });
    }
  }

  /**
   * Reloads the wish list and remembers which list was opened.
   */
  reloadWishlist(root: HTMLElement): void {
    this.createWishList(root);
  }

  /**
   * Create a hint that tells the user, that the given wish list is empty.
   * @param root: Root element of the wish list
   * @param wishListEmptyListHint HTML element that should contain the empty state hint.
   */
  addTranslationToEmptyStateHint(root: HTMLElement, wishListEmptyListHint: HTMLElement): HTMLElement {
    // list is empty, so display the empty list hint inside the accordion
    const emptyListHint = root.dataset.emptyListHint;
    if (emptyListHint) {
      // get the translation for the empty list hint and insert the pin icon at the correct position
      const listHintComponents = emptyListHint.split('%pin-icon-placeholder%');
      if (listHintComponents && listHintComponents.length > 1) {
        const emptyListTextBeforeIcon = document.createTextNode(listHintComponents[0]);
        const emptyListTextAfterIcon = document.createTextNode(listHintComponents[1]);
        const emptyListHintElement = createHtmlElementFromString(Icon.pin);
        wishListEmptyListHint.appendChild(emptyListTextBeforeIcon);
        wishListEmptyListHint.appendChild(emptyListHintElement);
        wishListEmptyListHint.appendChild(emptyListTextAfterIcon);
        return wishListEmptyListHint;
      }
    }
  }

  /**
   * Create a wish list item (html) with the given data and return the element.
   * @param root Root element of the wish list that contains the translation attributes.
   * @param wishListItemData Product information.
   */
  createWishListItem(root: HTMLElement, wishListItemData: HamiltonProduct): HTMLElement {
    const wishListItemMarkup = createHtmlElementFromString(this.wishListItemMarkup);

    // fill with the product details
    this.fillWithProductDetails(root, wishListItemMarkup, wishListItemData);

    // add translation for shopping cart button label (only in wish list items, not easy reference cards)
    const buttonLabel = document.createTextNode(root.dataset.addToCartButtonLabel);
    wishListItemMarkup.querySelector<HTMLElement>('.wish-list-item__add-to-cart-button').appendChild(buttonLabel);

    return wishListItemMarkup;
  }

  /**
   * Initialize all wish lists on the current page.
   * @param observe Boolean if the WishListModule should listen for changes in the DOM and initialize dynamically
   *   added elements
   */
  initializeAll(observe: boolean): void {
    const allComponents = document.querySelectorAll(this.componentSelector);
    for (let i = 0; i < allComponents.length; i++) {
      this.initialize(allComponents[i] as HTMLElement);
    }

    if (observe) {
      this.startListening();
    }
  }

  /**
   * Listen for changes in DOM and initialize elements when new ones appear
   */
  startListening(): void {
    componentObserver.subscribeListener(this);
  }

  /**
   * Expand button was clicked.
   */
  toggleWishListExpandedState(event: MouseEvent): void {
    const expandButton = event.currentTarget as HTMLElement;
    const wishListElement = expandButton.parentElement;

    // mark the expanded state using a class on the root element
    wishListElement.classList.toggle(this.expandedClass);

    // set aria attribute
    const isExpanded = wishListElement.classList.contains(this.expandedClass);
    expandButton.setAttribute('aria-expanded', isExpanded.toString());
  }

  /**
   * Set the selected state of the trigger button. Adds the primary state (blue) of the button
   * that shows that the wish list is currently open.
   * @param triggerButton: The trigger button HTML-Element
   * @param selected: If the button should be selected.
   */
  setTriggerButtonSelectedState(triggerButton: HTMLElement, selected: boolean): void {
    triggerButton?.classList.toggle('button--primary', selected);
  }

  /**
   * Hides button that open the wish list.
   */
  hideTriggerButton(): void {
    document.documentElement.classList.add('user-logged-out');
  }

  /**
   * Deselect the trigger button. Removes the primary state (blue) after the wish list
   * was closed.
   */
  deselectTriggerButton(): void {
    const triggerButton = document.querySelector('#header-button-wishlist');
    triggerButton?.classList.remove('button--primary');
  }

  /**
   * Show/hide the wish list when the trigger button was clicked.
   */
  toggleWishListVisibleState(root: HTMLElement): void {
    root.classList.toggle(this.visibleClass);
    // set aria attribute
    const isHidden = !root.classList.contains(this.visibleClass);
    root.setAttribute('aria-hidden', isHidden.toString());
    if (!isHidden) {
      // prevent scroll on body
      const breakpoint = currentBreakpoint();
      if (breakpoint.isMobileAny) {
        preventScrollingOnBody();
      }

      // reload the wish list before opening it
      this.reloadWishlist(root);
      //this.getWishlistStates();
    } else {
      // deselect all wish list buttons
      this.deselectTriggerButton();
      // remove the no scroll classes
      allowScrollingOnBody();
      this.setWishlistStates()
    }
  }

  /**
   * Close an open wishlist.
   */
  closeWishlist(): void {
    const wishListElement = document.querySelector('.wish-list');
    wishListElement.classList.remove(this.visibleClass);

    // deselect the trigger (favorites) button
    const wishListId = wishListElement.getAttribute('id');
    const triggerButton = document.querySelector<HTMLElement>(`[data-wish-list-id='${wishListId}']`);
    this.setTriggerButtonSelectedState(triggerButton, false);

    // remove scroll classes on body
    allowScrollingOnBody();

    setTimeout(() => {
      if (window.localStorage && window.localStorage.getItem('scrollPosition')) {
        window.scroll(0, parseInt(window.localStorage.getItem('scrollPosition')));
        console.log("Removing scroll pos wishlist");
        window.localStorage.removeItem('scrollPosition');
      }
    }, 50);
  }

  /**
   * Initialize open wishlist trigger (button). Opens the sidebar on click only on mobile.
   * @param root The wish list root element
   */
  initializeOpenWishListTrigger(root: HTMLElement): void {
    const wishListId = root.getAttribute('id');
    const triggerButton = document.querySelector<HTMLElement>(`[data-wish-list-id='${wishListId}']`);
    if (triggerButton) {
      triggerButton.addEventListener('click', () => {
        this.toggleWishListVisibleState(root);

        const isVisible = root.classList.contains(this.visibleClass);
        this.setTriggerButtonSelectedState(triggerButton, isVisible);
      });
    }
  }

  /**
   * Initializes all overlays that are related to the wish list.
   */
  initializeOverlays(root: HTMLElement): void {
    this.initializeNewWishListOverlay(root);
    this.initializeRenameWishListOverlay(root);
    this.initializeDeleteWishListOverlay(root);
  }

  /**
   * Initialize the 'new wish list' overlay.
   */
  initializeNewWishListOverlay(root: HTMLElement): void {
    const overlay: HTMLElement = document.querySelector(this.newWishListOverlaySelector);
    const createListButton = overlay.querySelector<HTMLButtonElement>('.button--primary');
    const wishListNameInput = overlay.querySelector('input');

    // automatically disable/enable the save button depending on the input value
    this.setupWishListOverlayInputField(wishListNameInput, createListButton);

    // listen for clicks on the 'create list' button
    createListButton.addEventListener('click', () => {
      // disable the button so that the user does not click twice
      createListButton.disabled = true;

      const newListName = wishListNameInput.value;
      createNewWishlist(newListName).then((success) => {
        if (success) {
          overlayModule.close(overlay);
          this.createWishList(root);

          // clear input field
          wishListNameInput.value = '';
        } else {
          const errorMessage = root.dataset.errorMessage;
          toastNotification.createErrorToastMessage(errorMessage);
        }

        // enable the button again
        createListButton.disabled = false;
      });
    });
  }

  /**
   * Initialize the 'rename wish list' overlay.
   */
  initializeRenameWishListOverlay(root: HTMLElement): void {
    const overlay: HTMLElement = document.querySelector(this.renameWishListOverlaySelector);
    const saveNameButton = overlay.querySelector('.button--primary');
    const wishListNameInput = overlay.querySelector('input');

    // automatically disable/enable the save button depending on the input value
    this.setupWishListOverlayInputField(wishListNameInput, saveNameButton);

    // listen for clicks on the 'save list' button
    saveNameButton.addEventListener('click', () => {
      const newListName = wishListNameInput.value;
      const listToRenameId = overlay.getAttribute('data-overlay-associated-id');
      renameWishlist(listToRenameId, newListName).then((success) => {
        if (success) {
          overlayModule.close(overlay);
          this.reloadWishlist(root);
        } else {
          const errorMessage = root.dataset.errorMessage;
          toastNotification.createErrorToastMessage(errorMessage);
        }
      });
    });
  }

  /**
   * Listens for changes in the given input field and disables/enables the given button
   * as soon as the text field is empty/not empty
   */
  setupWishListOverlayInputField(wishListNameInput: HTMLInputElement, saveNameButton: Element): void {
    // listen for changes in the text field
    wishListNameInput.addEventListener('input', () => {
      const currentInput = wishListNameInput.value;

      // if the list name is valid, enable the 'create list' button
      if (currentInput && currentInput.length > 0) {
        saveNameButton.removeAttribute('disabled');
      } else {
        saveNameButton.setAttribute('disabled', 'true');
      }
    });
  }

  /**
   * Initialize the 'delete wish list' overlay.
   */
  initializeDeleteWishListOverlay(root: HTMLElement): void {
    const overlay: HTMLElement = document.querySelector(this.deleteWishListOverlaySelector);
    const cancelButton = overlay.querySelector('#cancel-delete-wish-list-button');
    const deleteButton = overlay.querySelector('#confirm-delete-wish-list-button');

    // listen for clicks on the 'cancel' button
    cancelButton.addEventListener('click', () => {
      overlayModule.close(overlay);
    });

    // listen for clicks on the 'delete' button
    deleteButton.addEventListener('click', () => {
      const listToDeleteId = overlay.getAttribute('data-overlay-associated-id');
      deleteWishlist(listToDeleteId).then((success) => {
        if (success) {
          overlayModule.close(overlay);
          this.createWishList(root);
        } else {
          const errorMessage = root.dataset.errorMessage;
          toastNotification.createErrorToastMessage(errorMessage);
        }
      });
    });
  }

  /**
   * Open the wish list if it is currently closed. Reloads the items in the wish list.
   */
  openWishList(): void {
    const wishListElement = document.querySelector<HTMLElement>('.wish-list');
    const isHidden = !wishListElement.classList.contains(this.visibleClass);

    if (isHidden) {
      this.toggleWishListVisibleState(wishListElement);
    }
  }

  /**
   * Initialize the given wish list element.
   * @param root The wish list root element that should be initialized
   */
  initialize(root: HTMLElement): void {
    componentObserver.markElementAsInitialized(this.componentSelector, root);

    // initialize the wish list overlays
    this.initializeOverlays(root);

    // listen for clicks on the expand button
    root
      .querySelector('.wish-list__expand-button')
      .addEventListener('click', this.toggleWishListExpandedState.bind(this), false);
    // listen for clicks on the close button
    root
      .querySelector('.wish-list__close-button')
      .addEventListener('click', this.toggleWishListVisibleState.bind(this, root), false);

    // Add ESC key listener to close wish list
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && root.classList.contains(this.visibleClass)) {
        this.closeWishlist();
      }
    };

    // Add the event listener to document when wish list becomes visible
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target as HTMLElement;
          if (target.classList.contains(this.visibleClass)) {
            document.addEventListener('keydown', handleEscKey);
          } else {
            document.removeEventListener('keydown', handleEscKey);
          }
        }
      });
    });

    observer.observe(root, {
      attributes: true,
      attributeFilter: ['class']
    });

    // close the wish list when breakpoint changes from mobile -> desktop and the other way round
    breakpointListener.listenForBreakpointChangeBetweenMobileAndDesktop(() => {
      this.closeWishlist();
    });

    this.initializeOpenWishListTrigger(root);
    this.createWishList(root);
  }
}

const wishListModule = new WishListModule();
export { wishListModule as wishList };
